<template>
  <Loading v-if="loading" />
  <div class="infoLocker" v-if="order">
    <div class="infoLocker__header">
      <div class="infoLocker__header-title">
        <p>Locker {{ activeLocker }}</p>
        <div
          class="infoLocker__header-title-temperature"
          v-if="order.temperature"
        >
          <p
            :class="{
              temperatureCorrect: !getIncreasedOrDecreasedTemp,
              temperatureIncorrect: getIncreasedOrDecreasedTemp
            }"
          >
            {{ order.temperature }}°C
          </p>
        </div>
      </div>
      <div
        :class="{
          'infoLocker__header-toggle': true,
          tootip:
            !activePickupBox?.info.active ||
            !user.role.permissions.pickupbox.update
        }"
      >
        <FormKit
          type="form"
          id="disableLocker-form"
          v-model="formData"
          :actions="false"
          @submit="submitHandler"
        >
          <Toggle
            :title="$t('disableLocker')"
            :value="valueToggle()"
            name="inactive"
            variant="backgraund"
            :disabled="isDisabled()"
            @change="changeDisableLocker"
          />
          <span v-if="!activePickupBox?.info.active" class="tooltiptext">
            {{ $t('pickupboxIsInactive') }}
          </span>
          <span
            v-if="!user.role.permissions.pickupbox.update"
            class="tooltiptext"
          >
            {{ $t('enoughPermissions') }}
          </span>
        </FormKit>
      </div>
    </div>
    <div class="infoLocker__actions">
      <div class="infoLocker__actions-action">
        <div class="locker-size">
          <p class="size">{{ order.size.icon }}</p>
        </div>
        <p>{{ $t(order?.size?.label || '') }}</p>
      </div>
      <div class="infoLocker__actions-action">
        <img
          class="statusImg"
          :src="
            require(`@/assets/icons/${
              status in allStatus ? allStatus[status] : status
            }.svg`)
          "
          alt="status"
        />
        <p class="name">
          {{
            status in orderStatus && orderStatus[status]
              ? $t(orderStatus[status])
              : status
          }}
        </p>
      </div>
    </div>
    <div
      class="infoLocker__errorTemperature"
      v-if="getIncreasedOrDecreasedTemp"
    >
      <img src="@/assets/icons/Error.svg" alt="" />
      <div class="infoLocker__errorTemperature-messages">
        <div class="message">
          <p>
            {{
              $t(
                getMaxTemp ? 'messageTemperatureHigh' : 'messageTemperatureLow'
              )
            }}
          </p>
          <p>{{ loadingTempAlarms() }}</p>
        </div>
      </div>
    </div>
    <Order
      :order="order"
      :activePickupBox="activePickupBox"
      :activeLocker="activeLocker"
      :showDecodedSsn="showDecodedSsn"
      @getDecodedSsn="getDecodedSsn"
    />
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import moment from 'moment';

import Order from './Order/Order.vue';
import Loading from '@/views/Loading.vue';

import { orderStatus } from './configs';
import { allStatus } from '@/constants';
import { AlarmsData } from '@/store/modules/pharmacy/types';

@Options({
  name: 'InfoLocker',
  emits: ['updateLockerDataCallback', 'getDecodedSsn'],
  props: [
    'order',
    'activeLocker',
    'disableLocker',
    'lockerData',
    'activePickupBox'
  ],
  components: {
    Order,
    Loading
  },
  computed: {
    ...mapState('pharmacy', ['alarms', 'decodedSsn']),
    ...mapState('account', ['user']),
    status() {
      if (this.order.deliveryStatus) {
        return this.order.deliveryStatus;
      } else {
        return this.order.status;
      }
    },
    getFridgeMax() {
      return (
        this.order.temperature >
        this.activePickupBox.info.temperatureRangeFridge.max
      );
    },
    getFridgeMin() {
      return (
        this.order.temperature <
        this.activePickupBox.info.temperatureRangeFridge.min
      );
    },
    getBasicMax() {
      return (
        this.order.temperature >
        this.activePickupBox.info.temperatureRangeBasic.max
      );
    },
    getBasicMin() {
      return (
        this.order.temperature <
        this.activePickupBox.info.temperatureRangeBasic.min
      );
    },
    getMaxTemp() {
      return this.lockerData.fridge ? this.getFridgeMax : this.getBasicMax;
    },
    getIncreasedOrDecreasedTemp() {
      return this.getFridgeMinMax || this.getBasicMinMax;
    },
    getFridgeMinMax() {
      return this.lockerData.fridge && (this.getFridgeMax || this.getFridgeMin);
    },
    getBasicMinMax() {
      return !this.lockerData.fridge && (this.getBasicMax || this.getBasicMin);
    }
  },
  data() {
    return {
      formData: {
        inactive: false,
        lockerId: ''
      },
      orderStatus,
      allStatus,
      alarmsTempList: [],
      loading: false,
      showDecodedSsn: ''
    };
  },
  methods: {
    ...mapActions('pharmacy', [
      'getAlarms',
      'toggleLockerPickupBox',
      'getPickupBoxesByPharmacy',
      'getDeliveryShowDecodedSsn'
    ]),
    async loadingAlarms() {
      const boxId = this.activePickupBox.id;

      const endDay = new Date();

      const defaultParams = {
        boxId,
        endTime: endDay.valueOf(),
        startTime: new Date(
          endDay.getFullYear(),
          endDay.getMonth(),
          1
        ).valueOf()
      };

      await this.getAlarms(defaultParams);
    },
    loadingTempAlarms() {
      const alarm = [...this.alarms].find(
        (item: AlarmsData) => item['lockerId'] === this.activeLocker
      );

      return moment(alarm?.timestamp).format('DD.MM.YYYY HH:mm');
    },
    valueToggle() {
      this.formData.inactive = this.lockerData.inactive;

      if (this.formData.inactive) {
        this.lockerData.status = 'inactive';
        this.order.status = this.lockerData.status;
      } else {
        this.lockerData.status = 'free';
        this.order.status = this.lockerData.status;
      }

      return this.formData.inactive;
    },
    async submitHandler() {
      const boxId = this.activePickupBox.id;

      this.loading = true;

      this.formData = {
        ...this.formData,
        inactive: this.formData.inactive,
        lockerId: this.disableLocker
      };

      await this.toggleLockerPickupBox({
        data: this.formData,
        boxId
      });

      const { id } = this.$route.params;
      await this.getPickupBoxesByPharmacy(id);
      this.loading = false;

      this.$emit('updateLockerDataCallback');
    },
    changeDisableLocker() {
      this.formData.inactive = !this.formData.inactive;
      this.submitHandler();
    },
    isDisabled() {
      if (
        this.lockerData.state === 'disabled' ||
        this.lockerData.order ||
        !this.activePickupBox?.info.active ||
        !this.user.role.permissions.pickupbox.update
      ) {
        return true;
      }
      return false;
    },
    async getDecodedSsn() {
      const params = {
        deliveryId: this.order.deliveryId,
        reservedTime: this.order.reservedTime,
        pickupboxId: this.activePickupBox.id
      };

      await this.getDeliveryShowDecodedSsn(params);
      this.showDecodedSsn = this.decodedSsn.ssn;
    }
  },
  watch: {
    activePickupBox() {
      if (this.activePickupBox) {
        this.loadingAlarms();
      }
    },
    order() {
      this.showDecodedSsn = '';
    }
  }
})
export default class InfoLocker extends Vue {}
</script>

<style lang="scss" scoped>
.infoLocker {
  height: 100%;
  width: 100%;
  min-height: 610px;
  background-color: $light-grey;
  border: 1px solid $border;
  border-radius: 8px;
  margin-right: 24px;
  .infoLocker__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    border-bottom: 1px solid $border;
    background-color: $light-grey;
    .infoLocker__header-title {
      display: flex;
      align-items: center;
      max-width: 150px;
      width: 100%;
      column-gap: 12px;
      height: 56px;
      padding: 0 17px;
      border-top-left-radius: 8px;
      background-color: $light-grey;
      p {
        background-color: $light-grey;
        font-family: 'Ubuntu-Medium';
      }
      .infoLocker__header-title-temperature {
        max-width: 50px;
        width: 100%;
        .temperatureCorrect {
          text-align: center;
          padding: 4px 0;
          border-radius: 3px;
          color: $white;
          font-size: $tenSize;
          background-color: $main-green;
        }
        .temperatureIncorrect {
          text-align: center;
          padding: 4px 0;
          border-radius: 3px;
          color: $white;
          font-size: $tenSize;
          background-color: $red;
        }
      }
    }
    .tootip {
      position: relative;
      .tooltiptext {
        position: absolute;
        top: -65%;
        left: 50%;
        transform: translate(-50%, -100%);
        visibility: hidden;
        width: 150px;
        font-size: $elevenSize;
        line-height: $elevenSize;
        background: $label;
        color: $white;
        text-align: center;
        border-radius: 6px;
        padding: 5px;
        z-index: 1;
        opacity: 0;
        transition: opacity 0.3s;
        &::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          margin-left: -5px;
          border-width: 5px;
          border-style: solid;
          border-color: $label transparent transparent transparent;
        }
      }

      &:hover .tooltiptext {
        visibility: visible;
        opacity: 1;
      }
    }
  }
  .infoLocker__actions {
    display: flex;
    margin: 17px;
    background-color: $light-grey;
    .infoLocker__actions-action {
      display: flex;
      align-items: center;
      width: 140px;
      height: 41px;
      margin-right: 8px;
      background-color: $light-grey;
      border: 1px solid $border;
      border-radius: 8px;
      .locker-size {
        width: 19px;
        height: 19px;
        border: 1px solid $border;
        border-radius: 50%;
        margin-left: 12px;
        background-color: $light-grey;
        .size {
          margin-left: 0;
          margin-top: 4px;
          text-align: center;
          font-size: $twelveSize;
          background-color: $light-grey;
          border-radius: 8px;
        }
      }
      p {
        margin-left: 7px;
        color: $mid-grey;
        font-size: $fourteenSize;
        background-color: $light-grey;
      }
      img {
        margin-left: 16px;
      }
    }
  }
  .infoLocker__errorTemperature {
    display: flex;
    align-items: center;
    height: 35px;
    border: 1px solid $red;
    border-radius: 8px;
    margin: 0 17px 14px 17px;
    background: rgba(225, 82, 64, 0.06);
    img {
      margin-left: 17px;
      background: rgba(225, 82, 64, -0.94);
    }
    .infoLocker__errorTemperature-messages {
      border-radius: 8px;
      background: rgba(225, 82, 64, -0.94);
      .message {
        background: rgba(225, 82, 64, -0.94);
        p {
          display: inline-block;
          margin-left: 10px;
          color: $errorRed;
          background: rgba(225, 82, 64, -0.94);
        }
      }
    }
  }
}
</style>
